import React, { useEffect, useRef, useState } from "react";
import "./EditableGlobalImprovementsSurvey.scss";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  updateSurveyData,
  updateSurveyPage,
} from "../../../../../redux/actions/survey";
import SurveyHero from "../../../../components/Survey/SurveyHero/SurveyHero";
import HealthConcernText from "../../../../components/Survey/HealthConcernText/HealthConcernText";
import getThemes from "../../../../../constants/colors/getTheme";
import Button from "../../../../components/Comon/Button/Button";
import LinkText from "../../../../components/Comon/LinkText/LinkText";
import { resetPopUp, showPopup } from "../../../../../redux/actions/app";
import { updateSurvey } from "../../../../../core/axios/services";
import Loader from "../../../../components/Loader/Loader";
import {
  getConcernFieldByNameOrIndex,
  patchSurveyDataArrayItem,
} from "../../utils/editPayload";

const EditableGlobalImprovementsSurvey = ({
  showGlobalSnackbar,
  survey,
  i18n,
  app,
}) => {
  const [concerns, setConcerns] = useState([]);
  const [themeColor, setThemeColor] = useState([]);
  const [newScale, setNewScale] = useState(undefined);
  const [isSendingSurveyToApi, setIsSendingSurveyToApi] = useState(false);
  const initializedRef = useRef(false);

  const navigate = useNavigate();
  const { code } = useParams();
  const { surveyId } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    if (survey?.previousSurveyData?.scaleType || survey?.data?.scaleType) {
      setNewScale(
        (survey?.previousSurveyData?.scaleType ?? survey?.data?.scaleType) ===
          "zero_scale"
      );
    }
  }, [survey]);

  const getOveralHealthForCurrentLanguage = () => {
    return survey?.settings?.clinic?.overallHealths.find(
      (item) => item.language.id === survey?.settings?.language?.id
    );
  };

  const getGlobalHealthField = (name, fallbackIndex, field) =>
    getConcernFieldByNameOrIndex(
      survey?.data?.globalHealth,
      name,
      fallbackIndex,
      field
    );

  const buildGlobalHealthConcerns = () => {
    const overallHealth = getOveralHealthForCurrentLanguage();
    const bodyMetrics = Array.isArray(survey?.settings?.bodyMetrics)
      ? survey.settings.bodyMetrics
      : [];
    const bodyMetricCount = bodyMetrics.length;
    const markThingsOptions =
      getGlobalHealthField("markThings", bodyMetricCount + 4, "options") || [];

    const bodyMetricConcerns = bodyMetrics.map((metric, index) => ({
      name: metric.title,
      topText: metric.title,
      headline: metric.question,
      concernRate: getGlobalHealthField(metric.title, index, "concernRate"),
      leftOption: metric.lowScaleName,
      rightOption: metric.highScaleName,
      mandatory: false,
      hasTextInput: false,
      hasRate: true,
    }));

    const baseConcerns = [
      {
        name: "overallCurrent",
        topText: overallHealth?.question1Subtitle,
        headline: overallHealth?.question1Title,
        text: "",
        leftOption: overallHealth?.question1LowScale,
        rightOption: overallHealth?.question1HighScale,
        concernRate: getGlobalHealthField(
          "overallCurrent",
          bodyMetricCount,
          "concernRate"
        ),
        mandatory: false,
        hasTextInput: false,
        hasRate: true,
      },
      {
        name: "overallDesired",
        topText: overallHealth?.question2Subtitle,
        headline: overallHealth?.question2Title,
        text: "",
        leftOption: overallHealth?.question2LowScale,
        rightOption: overallHealth?.question2HighScale,
        concernRate: getGlobalHealthField(
          "overallDesired",
          bodyMetricCount + 1,
          "concernRate"
        ),
        mandatory: false,
        hasTextInput: false,
        hasRate: true,
      },
    ];

    if (survey?.settings?.type !== 2) {
      return [...bodyMetricConcerns, ...baseConcerns];
    }

    const advancedConcerns = [
      {
        name: "howLongToAchieve",
        topText: "",
        headline: overallHealth?.question4Question,
        concern:
          getGlobalHealthField("howLongToAchieve", bodyMetricCount + 2, "concern") ||
          "",
        mandatory: false,
        hasTextInput: true,
        hasRate: false,
        characterLimit: 120,
      },
      {
        name: "howImportant",
        topText: overallHealth?.question5Subtitle,
        headline: overallHealth?.question5Question,
        concernRate: getGlobalHealthField(
          "howImportant",
          bodyMetricCount + 3,
          "concernRate"
        ),
        leftOption: overallHealth?.question5LowScale,
        rightOption: overallHealth?.question5HighScale,
        mandatory: false,
        hasTextInput: false,
        hasRate: true,
      },
      {
        name: "markThings",
        topText: "",
        headline: overallHealth?.question6Question,
        text: "",
        options: [
          {
            id: 10,
            name: "firstTick",
            text: markThingsOptions?.[0]?.text ?? overallHealth?.question6Answer1,
            selected: markThingsOptions?.[0]?.selected ?? false,
          },
          {
            id: 11,
            name: "secondTick",
            text: markThingsOptions?.[1]?.text ?? overallHealth?.question6Answer2,
            selected: markThingsOptions?.[1]?.selected ?? false,
          },
          {
            id: 12,
            name: "thirdTick",
            text: markThingsOptions?.[2]?.text ?? overallHealth?.question6Answer3,
            selected: markThingsOptions?.[2]?.selected ?? false,
          },
          {
            id: 13,
            name: "fourthTick",
            text: markThingsOptions?.[3]?.text ?? overallHealth?.question6Answer4,
            selected: markThingsOptions?.[3]?.selected ?? false,
          },
          {
            id: 14,
            name: "fifthTick",
            text: markThingsOptions?.[4]?.text ?? overallHealth?.question6Answer5,
            selected: markThingsOptions?.[4]?.selected ?? false,
          },
          {
            id: 15,
            name: "sixthTick",
            text: markThingsOptions?.[5]?.text ?? overallHealth?.question6Answer6,
            isOther: true,
            selected: markThingsOptions?.[5]?.selected ?? false,
          },
        ],
        hasTextInput: false,
        hasRate: false,
      },
    ];

    return [...bodyMetricConcerns, ...baseConcerns, ...advancedConcerns];
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(updateSurveyPage(4));
  }, [dispatch]);

  useEffect(() => {
    initializedRef.current = false;
  }, [surveyId]);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!survey?.settings?.bodyMetrics || !survey?.data) return;

    const builtConcerns = buildGlobalHealthConcerns();
    if (!builtConcerns.length) return;

    setConcerns(builtConcerns);
    dispatch(
      updateSurveyData({
        ...survey.data,
        globalHealth: builtConcerns,
      })
    );
    initializedRef.current = true;
  }, [survey, i18n, dispatch]);

  useEffect(() => {
    if (app.popupStatus === true) {
      switch (app.popUpData?.type) {
        case "editSurvey":
          sendSurveyToApi();
          dispatch(resetPopUp());
          break;
        default:
          break;
      }
    } else if (app.popupStatus === false) {
      dispatch(resetPopUp());
    }

    setThemeColor(getThemes("mainColor", app.appTheme));
  }, [app, dispatch]);

  const sendSurveyToApi = async () => {
    setIsSendingSurveyToApi(true);
    try {
      const surveyPayload = {
        ...survey,
        data: {
          ...(survey?.data || {}),
          globalHealth: concerns,
        },
      };
      return await updateSurvey(surveyPayload).then(() => {
        setIsSendingSurveyToApi(false);
        dispatch(updateSurveyPage(5));
        navigate("/survey-edit/" + code + "/" + surveyId + "/thank-you");
      });
    } catch (error) {
      setIsSendingSurveyToApi(false);
      if (error.response?.data?.errorMessage) {
        showGlobalSnackbar(error.response.data.errorMessage);
      } else {
        showGlobalSnackbar(i18n.t("thereWasAnError"));
      }
    }
  };

  const nextPage = () => {
    if (survey.settings.type === 3) {
      dispatch(
        updateSurveyData({
          ...(survey?.data || {}),
          globalHealth: concerns,
        })
      );
      dispatch(updateSurveyPage(5));
      navigate("/survey-edit/" + code + "/" + surveyId + "/lifestyle-changes");
    } else {
      const data = {
        showPopup: true,
        data: {
          title: i18n.t("confirmEditSurvey"),
          confirmButton: i18n.t("confirmEditSurveyButton"),
          denyButton: i18n.t("denyEditSurveyButton"),
          type: "editSurvey",
        },
      };
      dispatch(showPopup(data));
    }
  };

  const previousPage = () => {
    dispatch(updateSurveyPage(3));
    navigate("/survey-edit/" + code + "/" + surveyId + "/health-concerns");
  };

  const updateConcern = (index, value, type) => {
    const fieldName = type === "text" ? "concern" : "concernRate";
    const nextConcerns = concerns.map((concern, concernIndex) =>
      concernIndex === index ? { ...concern, [fieldName]: value } : concern
    );
    setConcerns(nextConcerns);

    const nextData = patchSurveyDataArrayItem({
      data: survey.data,
      arrayKey: "globalHealth",
      name: nextConcerns[index]?.name,
      fallbackIndex: index,
      patch: { [fieldName]: value },
    });

    dispatch(
      updateSurveyData({
        ...(nextData || survey?.data || {}),
        globalHealth: nextConcerns,
      })
    );
  };

  const updateOptions = (data, index) => {
    const nextOptions = Array.isArray(data)
      ? data.map((option) => ({ ...option }))
      : data;
    const nextConcerns = concerns.map((concern, concernIndex) =>
      concernIndex === index ? { ...concern, options: nextOptions } : concern
    );
    setConcerns(nextConcerns);

    const nextData = patchSurveyDataArrayItem({
      data: survey.data,
      arrayKey: "globalHealth",
      name: nextConcerns[index]?.name,
      fallbackIndex: index,
      patch: { options: nextOptions },
    });

    dispatch(
      updateSurveyData({
        ...(nextData || survey?.data || {}),
        globalHealth: nextConcerns,
      })
    );
  };

  const scrollTo = () => {
    if (!concerns.length) return;
    const element = document.getElementById(concerns[0].name);
    element?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  const isBodyMetricConcern = (concernName) =>
    !["howLongToAchieve", "markThings"].includes(concernName);
  const globalImprovementsScaleVariant = "red_to_green";

  return (
    <div className="ThirdScreenSurvey">
      <SurveyHero
        headline={getOveralHealthForCurrentLanguage()?.title}
        text={getOveralHealthForCurrentLanguage()?.desc}
        videoLink={getOveralHealthForCurrentLanguage()?.videoUrl}
        buttonText={i18n.t("next")}
        clickedButton={() => scrollTo()}
        app={app}
        surveyType={survey?.settings?.type}
      />

      <div className={"concernsContainer"}>
        {concerns.map((concern, index) => {
          return (
            <div className={"concernContainer"} key={index}>
              <HealthConcernText
                newScale={newScale}
                scaleVariant={
                  isBodyMetricConcern(concern.name)
                    ? globalImprovementsScaleVariant
                    : "regular"
                }
                setConcernText={(value) => updateConcern(index, value, "text")}
                setConcernRate={(value) => updateConcern(index, value, "rate")}
                mandatory={concern.mandatory}
                text={concern.text}
                concernRate={concern.concernRate}
                topText={concern.topText}
                headline={concern.headline}
                themeColor={themeColor}
                hasTextInput={concern.hasTextInput}
                leftOption={concern.leftOption}
                rightOption={concern.rightOption}
                id={concern.name}
                i18n={i18n}
                hasRate={concern.hasRate}
                options={concern.options ? concern.options : null}
                setOptions={(optionsData) => updateOptions(optionsData, index)}
                concernText={concern.concern}
                characterLimit={concern.characterLimit}
              />
            </div>
          );
        })}
      </div>
      {isSendingSurveyToApi && <Loader />}
      <div className={"buttonsContainer"}>
        <div className={"nextContainer"} onClick={() => nextPage()}>
          <Button
            disabled={isSendingSurveyToApi}
            color={themeColor}
            mode={"small"}
            text={
              survey.settings.type === 3
                ? i18n.t("next")
                : i18n.t("completeSurvey")
            }
          />
        </div>
        <div className={"previousContainer"} onClick={() => previousPage()}>
          <LinkText text={i18n.t("previous")} />
        </div>
      </div>
    </div>
  );
};

export default EditableGlobalImprovementsSurvey;
